import type {EnvironmentContextType} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';

type ElementType = ExpressionElement['type'];

/**
 * The overlay targets of the nearest rich text that encloses a trigger, and
 * the position that rich text renders at. It keeps its identity while the rich
 * text is edited, and is replaced only when one of these values changes.
 */
export type EnclosingRichText = {
  readonly nativeIdPath: readonly NativeId[];
  readonly instanceIndexSuffix: string | undefined;
  // Target element ID to its element type.
  readonly targets: ReadonlyMap<string, ElementType>;
};

// Installed by a rich text without targets too, so it hides an outer one.
export const EMPTY_ENCLOSING_RICH_TEXT: EnclosingRichText = Object.freeze({
  nativeIdPath: [],
  instanceIndexSuffix: undefined,
  targets: new Map(),
});

const getInstanceIndexSuffix = (
  environmentContext: EnvironmentContextType
): string | undefined => {
  const instanceIndexSuffix = environmentContext.get('instanceIndexSuffix');
  return typeof instanceIndexSuffix === 'string'
    ? instanceIndexSuffix
    : undefined;
};

type SerializedEnclosingRichText = [
  nativeIdPath: NativeId[],
  instanceIndexSuffix: string | null,
  targets: Array<[id: string, type: ElementType]>,
];

/**
 * Returns the canonical key of the enclosing rich text built from these
 * arguments. It stays the same across edits to text, labels, instance props or
 * the order of instances, and `createEnclosingRichText` builds the scope from
 * it.
 */
export const getEnclosingRichTextKey = (
  nativeIdPath: readonly NativeId[],
  environmentContext: EnvironmentContextType,
  candidates: readonly ExpressionElement[]
): string => {
  const serialized: SerializedEnclosingRichText = [
    [...nativeIdPath],
    getInstanceIndexSuffix(environmentContext) ?? null,
    candidates
      .map(({id, type}): [string, ElementType] => [id, type])
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  ];
  return JSON.stringify(serialized);
};

export const createEnclosingRichText = (key: string): EnclosingRichText => {
  const [nativeIdPath, instanceIndexSuffix, targets] = JSON.parse(
    key
  ) as SerializedEnclosingRichText;

  return targets.length === 0
    ? EMPTY_ENCLOSING_RICH_TEXT
    : Object.freeze({
        nativeIdPath: Object.freeze(nativeIdPath),
        instanceIndexSuffix: instanceIndexSuffix ?? undefined,
        targets: new Map(targets),
      });
};
